import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from './cn';
import CtaLink from './CtaLink';
import { useTransparentHeader } from '../layout/headerState';
import { hideBoot } from '../../utils/boot';

/** Habillage de marque d'une slide: chaque slide a le sien, comme les images de la référence. */
export type HeroDecor = 'lines' | 'prism' | 'none';
const DECOR_CYCLE: HeroDecor[] = ['lines', 'none', 'prism'];

export type HeroSlide = { id: string; image: string; eyebrow?: string; title: string; text?: string; label: string; to: string; cta?: string; decor?: HeroDecor };

/** Lignes diagonales et aplat dans le coin bas-droit. */
function LinesDecor() {
  const lines = ['M0 0 L320 240 H540', 'M320 0 V240', 'M1110 900 L1440 560', 'M1118 640 V900 M1118 640 H1440'];
  return (
    <svg aria-hidden viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" className="pointer-events-none absolute inset-0 h-full w-full text-brand" fill="none" stroke="currentColor" strokeWidth="6">
      {lines.map((d, i) => (
        <path key={d} d={d} pathLength={1} className="hero-line" style={{ ['--i' as string]: i }} />
      ))}
      <polygon points="1230,560 1440,560 1440,880 1230,880" fill="currentColor" stroke="none" className="hero-fill" />
    </svg>
  );
}

/** Grand triangle translucide qui éclaircit la photo, bordé d'un trait de marque. */
function PrismDecor() {
  return (
    <svg aria-hidden viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" className="pointer-events-none absolute inset-0 h-full w-full text-brand" fill="none" stroke="currentColor" strokeWidth="5">
      <polygon points="800,0 1440,0 1440,900 1080,900" fill="rgba(255,255,255,0.09)" stroke="none" className="hero-tint" />
      <path d="M814 100 L1080 900" pathLength={1} className="hero-line" style={{ ['--i' as string]: 0 }} />
      <path d="M1440 120 L1180 380 H1440" pathLength={1} className="hero-line" style={{ ['--i' as string]: 1 }} />
    </svg>
  );
}

function Decor({ kind }: { kind: HeroDecor }) {
  if (kind === 'lines') return <LinesDecor />;
  if (kind === 'prism') return <PrismDecor />;
  return null;
}

/**
 * Hero d'accueil: plein écran, trois entrées différentes (montée, balayage diagonal, balayage inverse),
 * textes qui montent dans un masque, zoom lent de l'image, onglets dont le trait de progression
 * sert de minuteur, et bouton pause avec anneau de progression.
 */
export function HomeHero({ slides }: { slides: HeroSlide[] }) {
  useTransparentHeader();
  useEffect(() => hideBoot(450), []);
  const [active, setActive] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const clearTimer = useRef<number | undefined>(undefined);
  const count = slides.length;

  const goTo = useCallback((n: number) => {
    setActive((cur) => {
      if (n === cur) return cur;
      setPrev(cur);
      return n;
    });
  }, []);

  useEffect(() => {
    if (prev === null) return;
    window.clearTimeout(clearTimer.current);
    clearTimer.current = window.setTimeout(() => setPrev(null), 1000);
    return () => window.clearTimeout(clearTimer.current);
  }, [prev, active]);

  useEffect(() => { if (active >= count && count > 0) setActive(0); }, [active, count]);

  if (count === 0) return null;

  const next = () => goTo((active + 1) % count);

  return (
    <section className={cn('relative -mt-[5.5rem] h-screen min-h-[600px] overflow-hidden bg-charcoal text-white', paused && 'hero-paused')} aria-roledescription="carrousel">
      {slides.map((s, n) => {
        const state = n === active ? 'active' : n === prev ? 'prev' : 'idle';
        return (
          <div key={s.id} data-state={state} className={cn('hero-slide absolute inset-0', `hero-enter-${n % 3}`)} aria-hidden={state !== 'active'}>
            <img src={s.image} alt="" className="hero-img absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-black/10" />
            <Decor kind={s.decor ?? DECOR_CYCLE[n % DECOR_CYCLE.length]} />

            <div className="site-container relative z-10 flex h-full flex-col justify-center pb-32 pt-24">
              <div className="max-w-xl">
                {s.eyebrow && (
                  <div className="hero-mask relative mb-6 inline-block">
                    <div className="hero-reveal relative text-xs font-semibold uppercase tracking-[0.0625rem]" style={{ ['--d' as string]: '0.1s' }}>
                      <span aria-hidden className="absolute right-full top-1/2 mr-6 hidden h-[0.12rem] w-[7.5rem] -translate-y-1/2 bg-brand lg:block" />
                      {s.eyebrow}
                    </div>
                  </div>
                )}
                <div className="hero-mask pb-4">
                  <h1 className="hero-reveal hero-reveal-title -mb-4 text-inherit font-display text-[2.5rem] font-bold normal-case leading-none tracking-normal md:text-[3.875rem] xl:text-[4.5rem]" style={{ ['--d' as string]: '0.1s' }}>
                    {s.title}
                  </h1>
                </div>
                {s.text && (
                  <div className="hero-mask mt-6 pb-1">
                    <p className="hero-reveal max-w-md text-[1.0625rem] leading-relaxed text-white/90" style={{ ['--d' as string]: '0.15s' }}>{s.text}</p>
                  </div>
                )}
                <div className="hero-mask mt-6 inline-block">
                  <div className="hero-reveal" style={{ ['--d' as string]: '0.25s' }}>
                    <CtaLink to={s.to} tone="light">{s.cta ?? 'Découvrir'}</CtaLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div className="absolute inset-x-0 bottom-[4.19rem] z-10">
        <div className="site-container flex items-end justify-between gap-6">
          <ol className="flex flex-wrap gap-x-10 gap-y-3">
            {slides.map((sl, n) => (
              <li key={sl.id} style={{ ['--i' as string]: n }}>
                <button
                  onClick={() => goTo(n)}
                  aria-current={n === active}
                  className={cn('relative block overflow-hidden pb-1 pt-3 font-display text-lg font-medium transition-colors duration-[250ms] ease-out hover:text-white', n === active ? 'text-white delay-[400ms]' : 'text-[#cfcfcf]')}
                >
                  {/* La fin de l'animation du trait enchaîne la slide suivante */}
                  <span
                    key={n === active ? `bar-${active}` : 'bar'}
                    data-active={n === active}
                    className="hero-tab-bar"
                    onAnimationEnd={(e) => { if (n === active && e.animationName === 'hero-bar' && count > 1) next(); }}
                  />
                  <span className="hero-tab-text">{sl.label}</span>
                </button>
              </li>
            ))}
          </ol>

          <button
            aria-label={paused ? 'Reprendre le carrousel' : 'Mettre le carrousel en pause'}
            onClick={() => setPaused((p) => !p)}
            className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-[0.13rem] border-white text-white transition-colors duration-[250ms] hover:border-brand-dark hover:bg-brand-dark hover:text-white"
          >
            <svg aria-hidden viewBox="0 0 42 42" className="pointer-events-none absolute left-1/2 top-1/2 h-[2.625rem] w-[2.625rem] -translate-x-1/2 -translate-y-1/2 -rotate-90" fill="none">
              <circle key={`ring-${active}`} data-active className="hero-ring" cx="21" cy="21" r="19" pathLength={1} stroke="var(--color-brand)" strokeWidth="2" />
            </svg>
            {paused ? (
              <svg viewBox="0 0 10 12" className="h-3 w-3 pl-0.5" fill="currentColor"><path d="M1 0l9 6-9 6z" /></svg>
            ) : (
              <svg viewBox="0 0 10 12" className="h-3 w-3" fill="currentColor"><path d="M0 0h3v12H0zM7 0h3v12H7z" /></svg>
            )}
          </button>
        </div>
      </div>
    </section>
  );
}

/** Fond sombre affiché pendant le chargement des slides, pour éviter un saut de mise en page. */
export function HeroPlaceholder() {
  useTransparentHeader();
  // Le loader prend le relais de l'écran de démarrage une fois la transition d'entrée de la page terminée.
  useEffect(() => hideBoot(450), []);
  return (
    <section aria-hidden className="relative -mt-[5.5rem] flex h-screen min-h-[600px] items-center justify-center overflow-hidden bg-charcoal">
      <img src="/logo.png" alt="" className="hero-loader-logo h-20 w-auto brightness-0 invert" />
      <span className="hero-loader absolute inset-x-0 bottom-0 h-1" />
    </section>
  );
}

/** Hero de page intérieure: image sombre, eyebrow, titre, texte et bouton optionnels. */
export function PageHero({ image, eyebrow, title, text, children, size = 'lg' }: { image?: string; eyebrow?: string; title: string; text?: string; children?: ReactNode; size?: 'sm' | 'lg' }) {
  useTransparentHeader();
  return (
    <section className={cn('relative -mt-[5.5rem] overflow-hidden bg-charcoal text-white', size === 'lg' ? 'min-h-[36rem] md:min-h-[56rem]' : 'min-h-[26rem] md:min-h-[34rem]')}>
      {image && <img src={image} alt="" className="zoom-slow absolute inset-0 h-full w-full object-cover object-[center_25%] opacity-70" />}
      <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/40 to-black/10" />
      <svg aria-hidden viewBox="0 0 1440 600" preserveAspectRatio="xMaxYMax slice" className="pointer-events-none absolute inset-0 h-full w-full text-steel-soft" fill="none" stroke="currentColor" strokeWidth="2" strokeOpacity="0.5">
        <path d="M1440 330 L1170 600" /><path d="M1440 410 L1250 600" />
      </svg>
      <div className="site-container relative flex min-h-[inherit] flex-col justify-end pb-14 pt-40 md:pb-20">
        <div className="max-w-3xl animate-reveal">
          {eyebrow && <div className="mb-4 text-xs font-semibold uppercase tracking-[0.0625rem]">{eyebrow}</div>}
          <h1 className="text-inherit font-display text-[2.75rem] font-bold normal-case leading-[1.05] tracking-normal md:text-[4rem]">{title}</h1>
          {text && <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/90">{text}</p>}
          {children && <div className="mt-8">{children}</div>}
        </div>
      </div>
    </section>
  );
}
